package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_user_not_found
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.WeeklyAvailabilityTemplate
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.availability.DeleteWeeklyAvailabilityTemplateUseCase
import com.evoapps.evoshift.domain.usecase.availability.GetWeeklyAvailabilityTemplatesUseCase
import com.evoapps.evoshift.domain.usecase.availability.SaveWeeklyAvailabilityTemplateUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class WeeklyTemplatesUiState(
    val isLoading: Boolean = true,
    val workplaceId: String = "",
    val userId: String = "",
    val templates: List<WeeklyAvailabilityTemplate> = emptyList(),
    val availableSlots: List<AvailabilitySlot> = emptyList(),
    val error: UiText? = null,
    val success: Boolean = false,
    val editingTemplate: WeeklyAvailabilityTemplate? = null,
    val firstDayOfWeek: Int = 1
)

sealed interface WeeklyTemplatesEvent {
    data class SaveTemplate(val id: String, val name: String, val days: Map<Int, DayPreference>) :
        WeeklyTemplatesEvent

    data class DeleteTemplate(val id: String) : WeeklyTemplatesEvent
    data class SetEditingTemplate(val template: WeeklyAvailabilityTemplate?) : WeeklyTemplatesEvent
    data object ClearMessage : WeeklyTemplatesEvent
}

class WeeklyTemplatesViewModel(
    private val initialEditTemplateId: String?,
    private val openNew: Boolean,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getWeeklyAvailabilityTemplatesUseCase: GetWeeklyAvailabilityTemplatesUseCase,
    private val saveWeeklyAvailabilityTemplateUseCase: SaveWeeklyAvailabilityTemplateUseCase,
    private val deleteWeeklyAvailabilityTemplateUseCase: DeleteWeeklyAvailabilityTemplateUseCase,
    private val getWorkplaceUseCase: GetWorkplaceUseCase,
    private val sessionManager: WorkplaceSessionManager
) : ViewModel() {

    private val _uiState = MutableStateFlow(WeeklyTemplatesUiState())
    val uiState = _uiState.asStateFlow()

    init {
        observeSession()
    }

    private fun observeSession() {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { wpId ->
                if (wpId != null) {
                    loadData(wpId)
                }
            }
        }
    }

    /** Full load for a workplace; the spinner shows only until that workplace's templates are on screen. */
    private fun loadData(workplaceId: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = it.isLoading || it.workplaceId != workplaceId, workplaceId = workplaceId) }

            val user = getCurrentUserUseCase().first()
            if (user == null) {
                _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_user_not_found)) }
                return@launch
            }

            val workplace = getWorkplaceUseCase(workplaceId).getOrNull()
            val slots = workplace?.settings?.definedAvailabilitySlots ?: emptyList()
            val firstDayOfWeek = workplace?.settings?.firstDayOfWeek ?: 1

            val templates = getWeeklyAvailabilityTemplatesUseCase(
                user.uid,
                workplaceId
            ).getOrDefault(emptyList())

            var editingTemplate: WeeklyAvailabilityTemplate? = null
            if (openNew) {
                val defaultDays = (1..7).associate { i ->
                    i to DayPreference(AvailabilityStatus.AVAILABLE, null, null)
                }
                editingTemplate = WeeklyAvailabilityTemplate(
                    id = "",
                    name = "",
                    userId = user.uid,
                    workplaceId = workplaceId,
                    days = defaultDays
                )
            } else if (initialEditTemplateId != null) {
                editingTemplate = templates.find { it.id == initialEditTemplateId }
            }

            _uiState.update {
                it.copy(
                    isLoading = false,
                    userId = user.uid,
                    templates = templates,
                    availableSlots = slots,
                    editingTemplate = editingTemplate,
                    firstDayOfWeek = firstDayOfWeek
                )
            }
        }
    }

    fun onEvent(event: WeeklyTemplatesEvent) {
        when (event) {
            is WeeklyTemplatesEvent.SaveTemplate -> saveTemplate(event.id, event.name, event.days)
            is WeeklyTemplatesEvent.DeleteTemplate -> deleteTemplate(event.id)
            is WeeklyTemplatesEvent.SetEditingTemplate -> _uiState.update { it.copy(editingTemplate = event.template) }
            WeeklyTemplatesEvent.ClearMessage -> _uiState.update {
                it.copy(
                    error = null,
                    success = false
                )
            }
        }
    }

    private fun saveTemplate(id: String, name: String, days: Map<Int, DayPreference>) {
        if (id.isPending) return
        val before = _uiState.value.templates
        val template = WeeklyAvailabilityTemplate(
            id = id,
            name = name,
            userId = _uiState.value.userId,
            workplaceId = _uiState.value.workplaceId,
            days = days
        )
        // A new template shows under a placeholder id until the re-read brings the generated one.
        val shown = if (id.isBlank()) template.copy(id = "$PENDING_ID_PREFIX${before.size}-$name") else template
        val templates = if (before.any { it.id == id }) before.map { if (it.id == id) shown else it } else before + shown
        _uiState.update { it.copy(templates = templates, editingTemplate = null) }

        viewModelScope.launch {
            saveWeeklyAvailabilityTemplateUseCase(template).fold(
                onSuccess = {
                    _uiState.update { it.copy(success = true) }
                    if (id.isBlank()) refreshTemplates()
                },
                onFailure = { e ->
                    // Back to the editor with what was typed, so nothing is lost.
                    _uiState.update { it.copy(templates = before, editingTemplate = template, error = e.toUiText()) }
                }
            )
        }
    }

    private fun deleteTemplate(id: String) {
        if (id.isPending) return
        val before = _uiState.value.templates
        _uiState.update { state -> state.copy(templates = before.filterNot { it.id == id }) }
        viewModelScope.launch {
            deleteWeeklyAvailabilityTemplateUseCase(id).onFailure { e ->
                _uiState.update { it.copy(templates = before, error = e.toUiText()) }
            }
        }
    }

    /** Re-reads only the list, without a spinner and without touching the editor. */
    private suspend fun refreshTemplates() {
        val state = _uiState.value
        getWeeklyAvailabilityTemplatesUseCase(state.userId, state.workplaceId).onSuccess { templates ->
            _uiState.update { it.copy(templates = templates) }
        }
    }
}
